<script setup lang="ts">
import { computed, ref } from 'vue';
import type { Project, UserProfile } from '@factoryfixinc/ats-interfaces';

import MeService from '@/core/shared/me/me.service';

import { getUserName } from '@/core/shared/user-profile/utils/get-user-name.util';

import EditIcon from '@/assets/svg/edit.svg?component';
import Checkbox from '@/components/Shared/Input/Checkbox/Checkbox.vue';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import ProjectService from '@/core/shared/project/project.service';

const projectService = new ProjectService();
const meService = new MeService();

interface SelectedTeamIds {
  watcherIds: number[];
  assigneeId: number | null;
}

interface TeamMember extends UserProfile {
  userName: string;
  isSelected: boolean;
  isOwner: boolean;
}

const props = withDefaults(
  defineProps<{
    project: Project;
    displayActivator: boolean;
    modelValue: boolean;
    isEmptyAvatar?: boolean;
  }>(),
  {
    isEmptyAvatar: false,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', payload: boolean): void;
}>();

const myUser = computed(() => meService.userProfile);

const isDisplayingActivator = computed(() => props.displayActivator);

const isOpen = computed<boolean>(() => {
  return props.modelValue;
});

// Teams
const selectedTeamIds = computed<SelectedTeamIds>(() => {
  const watcherIds = props.project.watchers?.map((watcher) => watcher.id) ?? [];
  const assigneeId = props.project.assignee?.id ?? null;

  return {
    watcherIds,
    assigneeId,
  };
});

const employerUsers = computed<TeamMember[]>((): TeamMember[] => {
  const teamMemberList =
    meService.employerUsers?.map((user) => {
      const isMe = user.id === myUser.value?.id;
      let userName = getUserName(user);
      let isSelected = false;
      let isOwner = false;

      if (isMe) {
        userName += ' (Me)';
      }

      if (selectedTeamIds.value.assigneeId === user.id) {
        isSelected = true;
        isOwner = true;
      } else if (selectedTeamIds.value.watcherIds.includes(user.id)) {
        isSelected = true;
      }

      return {
        ...user,
        userName,
        isSelected,
        isOwner,
      };
    }) ?? [];

  teamMemberList.sort((a, b) => {
    if (a.userName?.toLowerCase() < b.userName?.toLowerCase()) {
      return -1;
    }
    return 1;
  });

  return teamMemberList;
});

function handleUpdateModelValue(value: boolean) {
  emit('update:modelValue', value);
}

// Ignore clicks while a team update is in flight, so a slow earlier update can't undo a later one
const isSaving = ref(false);

async function handleTeamMemberClick(employerUser: TeamMember) {
  if (isSaving.value) return;
  isSaving.value = true;

  const isTeamEmpty = !employerUsers.value.some((user) => user.isSelected);

  try {
    if (employerUser.isOwner) {
      // Unchecking the owner takes them off the team, including a watcher row left by "Set as owner"
      if (selectedTeamIds.value.watcherIds.includes(employerUser.id)) {
        await projectService.removeWatcher(props.project.id, employerUser.id);
      }
      await projectService.updateAssignee(props.project.id, null);
    } else if (isTeamEmpty) {
      // The first member added to an empty team becomes its owner
      await projectService.updateAssignee(props.project.id, employerUser.id, { ...employerUser });
    } else if (employerUser.isSelected) {
      await projectService.removeWatcher(props.project.id, employerUser.id);
    } else {
      await projectService.addWatcher(props.project.id, employerUser.id, {
        nameFirst: employerUser.nameFirst,
        nameLast: employerUser.nameLast,
        id: employerUser.id,
      });
    }

    // Update selected project with new team members
    await projectService.getProjectById(props.project.id);
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to update team members');
  } finally {
    isSaving.value = false;
  }
}

async function handleOwnerChange(employerUser: TeamMember) {
  if (isSaving.value) return;
  isSaving.value = true;

  try {
    const currentOwner = employerUsers.value.find((user) => user.isOwner);

    if (currentOwner && !selectedTeamIds.value.watcherIds.includes(currentOwner.id)) {
      await projectService.addWatcher(props.project.id, currentOwner.id, {
        nameFirst: currentOwner.nameFirst,
        nameLast: currentOwner.nameLast,
        id: currentOwner.id,
      });
    }

    await projectService.updateAssignee(props.project.id, employerUser.id, { ...employerUser });

    // Update selected project with new team members
    await projectService.getProjectById(props.project.id);
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to update owner');
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <v-menu
    :model-value="isOpen"
    :close-on-content-click="false"
    location="bottom"
    max-width="350"
    min-width="256"
    max-height="350"
    @update:model-value="handleUpdateModelValue"
  >
    <template #activator="{ props }">
      <div
        v-bind="props"
        class="absolute left-1 top-1 flex cursor-pointer items-center justify-center rounded-md"
        :class="{
          'opacity-0': !isDisplayingActivator,
          'opacity-1': isDisplayingActivator,
        }"
      >
        <EditIcon
          class="inline-block cursor-pointer"
          :class="{ 'stroke-tint-100': isEmptyAvatar }"
          height="16"
          width="16"
        />
      </div>
    </template>

    <div class="menu-card !block w-[256px] space-y-0.5 overflow-y-auto md:w-[350px]">
      <div
        v-for="employerUser in employerUsers"
        :key="employerUser.id"
        class="menu-item h-10 cursor-pointer"
        :class="{
          'menu-row-selected': employerUser.isSelected,
        }"
        @click="handleTeamMemberClick(employerUser)"
      >
        <div class="flex !w-[calc(100%-64px)] items-center">
          <Checkbox :model-value="employerUser.isSelected" />
          <span class="min-w-0 truncate">{{ employerUser.userName }}</span>
        </div>
        <div class="w-[64px] text-right text-[10px] font-bold">
          <span v-if="employerUser.isOwner">Owner</span>
          <p
            v-else-if="employerUser.isSelected"
            class="text-highlight-600"
            @click.stop="handleOwnerChange(employerUser)"
          >
            Set as owner
          </p>
        </div>
      </div>
    </div>
  </v-menu>
</template>
